import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { toast } from "sonner";
import EmailToast from "./EmailToast";
import useLayoutStore from "@/stores/useLayoutStore";
import { SUBSCRIBED_LOCAL_STORAGE_KEY } from "@/utils/constants";

const POPUP_REAPPEAR_DELAY_MS = 17.5 * 24 * 60 * 60 * 1000; // 2.5 weeks
const SCROLL_THRESHOLD = 0.75;
const ALLOWED_PATHS = ["/", "/product"];

export default function EmailToastTrigger() {
  const { pathname } = useRouter();
  const hasFired = useRef(false);
  const emailToastId = useRef<string | number | null>(null);
  const isSideCartOpen = useLayoutStore((state) => state.isSideCartOpen);
  const isMenuOpen = useLayoutStore((state) => state.isMenuOpen);

  useEffect(() => {
    hasFired.current = false;
  }, [pathname]);

  // dismiss email toast when cart or mobile menu opens
  useEffect(() => {
    if ((isSideCartOpen || isMenuOpen) && emailToastId.current) {
      const id = emailToastId.current;
      emailToastId.current = null;
      // delay to avoid dismissing mid-render when sonner is updating
      setTimeout(() => toast.dismiss(id), 50);
    }
  }, [isSideCartOpen, isMenuOpen]);

  useEffect(() => {
    if (!ALLOWED_PATHS.includes(pathname)) {
      if (emailToastId.current) {
        const id = emailToastId.current;
        emailToastId.current = null;
        setTimeout(() => toast.dismiss(id), 50);
      }
      return;
    }

    const fireToast = () => {
      if (hasFired.current) return;
      hasFired.current = true;

      emailToastId.current = toast.custom((id) => <EmailToast toastId={id} />, {
        toasterId: "global",
        duration: Infinity,
        classNames: {
          toast:
            "!bg-transparent !border-none !shadow-none !p-0 w-[var(--width)]",
        },
      });
    };

    const handleScroll = () => {
      if (hasFired.current) return;

      const progress =
        window.scrollY /
        (document.documentElement.scrollHeight - window.innerHeight);
      if (progress < SCROLL_THRESHOLD) return;

      window.removeEventListener("scroll", handleScroll);

      const now = Date.now();
      try {
        const isSubscribed =
          localStorage.getItem(SUBSCRIBED_LOCAL_STORAGE_KEY) === "true";
        const lastPopupTime = localStorage.getItem("lastPopupTime");
        const hasPassedDelay =
          lastPopupTime &&
          now - Number(lastPopupTime) >= POPUP_REAPPEAR_DELAY_MS;

        if (isSubscribed) return;
        if (lastPopupTime && !hasPassedDelay) return;

        localStorage.setItem("lastPopupTime", now.toString());
      } catch (error) {
        console.error("Failed to access localStorage:", error);
      }
      if (document.querySelectorAll('[role="dialog"]').length > 0) return;
      fireToast();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [pathname]);

  return null;
}
